import { MbscCalendarEvent, MbscCalendarEventData } from '@mobiscroll/react';
import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { ContentCopy, Delete, DragIndicator } from '@mui/icons-material';
import {
	EmployeeStatusIndicator,
	EmployeeStatusInfo,
} from '../../components/EmployeeStatusIndicator';
import EmployeeAvailabilityIndicator from '../../components/EmployeeAvailabilityIndicator';
import TruncatedTooltip from '../../components/TruncatedTooltip/TruncatedTooltip';
import {
	getFullscreenPopperSlotProps,
	useFullscreenContainer,
} from 'core/utils/fullscreenContainer';
import {
	getBestContrastTextColor,
	getShiftActionIconButtonSx,
} from 'core/utils/colorContrast';
import { formatSchedulerTimeRange, formatSchedulerHours } from 'shared/utils/dates/formatSchedulerTime';
import {
	formatTimeOffScheduleLabel,
	isTimedTimeOff,
	timeOffOverlapsRange,
} from '../../utils/timeOffRequestForm';
import { canDeleteCalendarTimeOff } from '../../utils/timeOffCalendarActions';
import type { ScheduleResource as ScheduleResourceType } from 'types/scheduler';
import {
	isEmployeeResource,
	isOpenShiftResource,
	isJobCodeResource,
	isEventTypeResource,
} from 'types/scheduler';

interface ScheduleEventProps {
	calendarType: string;
	calendarView?: string; // 'day', 'week', or 'workweek'
	data: MbscCalendarEventData;
	onDeleteClick?: (event: MbscCalendarEvent) => void;
	onCopyClick?: (event: MbscCalendarEvent) => void;
	canDeleteShifts?: boolean;
	canManageAllRequests?: boolean;
	currentUserId?: string | number;
}

const getScheduleEventStyles = (original: MbscCalendarEvent, _calendarView?: string) => {
	const isTimeOffEvent = (original as any).isTimeOff;

	if (isTimeOffEvent) {
		return {
			cursor: 'pointer',
			color: '#ffffff',
			opacity: 0.9,
		};
	}

	const dynamicStyle = original.added_by_mabel
		? { border: '2px solid var(--color-purple)' }
		: { borderColor: original?.background_color };

	const backgroundColor = original?.background_color || '#446388';
	const textColor = getBestContrastTextColor(backgroundColor);

	return {
		cursor: 'pointer',
		backgroundColor: backgroundColor,
		color: textColor,
		...dynamicStyle,
	};
};

const getEventClassNames = (
	calendarType: string,
	original: MbscCalendarEventData['original'],
	_calendarView?: string
) => {
	const isTimeOffEvent = (original as any)?.isTimeOff;

	return [
		'md-timeline-template-event',
		original?.overlap ? 'hasStripeBackground' : '',
		calendarType === 'request' ? 'requestsEventCustom' : '',
		'scheduleEvent',
		isTimeOffEvent ? 'time-off-event' : '',
		original?.role === undefined && calendarType === 'request' ? 'timeoffColor' : '',
	]
		.filter(Boolean)
		.join(' ');
};

export const ScheduleEvent: React.FC<ScheduleEventProps> = ({
	calendarType,
	calendarView,
	data,
	onDeleteClick,
	onCopyClick,
	canDeleteShifts = false,
	canManageAllRequests = false,
	currentUserId,
}) => {
	const { original } = data!;
	const portalContainer = useFullscreenContainer();
	const tooltipSlotProps = getFullscreenPopperSlotProps(portalContainer);
	// console.log('ScheduleEvent original data:', original, data);

	const isRequest = original.role !== undefined || calendarType !== 'request';

	if (original?.shift_type?.id === 2 && original?.scheduled) return null;

	const handleDeleteClick = (e: React.MouseEvent) => {
		e.stopPropagation(); // Prevent event from triggering calendar's onEventClick
		if (onDeleteClick && original) {
			onDeleteClick(original);
		}
	};

	const handleCopyClick = (e: React.MouseEvent) => {
		e.stopPropagation(); // Prevent event from triggering calendar's onEventClick
		if (onCopyClick && original) {
			onCopyClick(original);
		}
	};

	// Build employee status array for events
	const employeeStatuses: EmployeeStatusInfo[] = [];

	// Check for time-off conflicts using employeeTimeOff data (specific date ranges)
	if (original?.employee && original?.startAt && original?.endAt) {
		const employeeTimeOff = (original as any).employeeTimeOff;
		if (employeeTimeOff && employeeTimeOff.length > 0) {
			// Only flag when the time-off window actually overlaps the shift's hours
			// (all-day time off still covers the whole day)
			const matchingTimeOff = employeeTimeOff.find((timeOff: any) =>
				timeOffOverlapsRange(timeOff.startAt, timeOff.endAt, original.startAt, original.endAt)
			);

			if (matchingTimeOff) {
				const timeOffMessage = `Employee has time off: ${formatTimeOffScheduleLabel(
					matchingTimeOff.startAt,
					matchingTimeOff.endAt
				)}`;

				employeeStatuses.push({
					type: 'time_off',
					label: 'Time Off Scheduled',
					details: timeOffMessage,
					severity: 'error',
				});
			}
		}
	}
	// console.log('Employee statuses after availability/time-off check:', original?.employeeStatuses, employeeStatuses);

	// Include other statuses from employeeStatusData if available
	if (original?.employeeStatusData) {
		const statusData = original.employeeStatusData;
		
		// console.log('ScheduleEvent statusData:', {
		// 	shiftId: original.id,
		// 	employee: original.employee,
		// 	hasAvailabilityIssue: statusData.hasAvailabilityIssue,
		// 	availabilityMessage: statusData.availabilityMessage,
		// });

		// Check for availability conflicts from statusData (calculated in SchedulePage)
		if (statusData.hasAvailabilityIssue) {
			employeeStatuses.push({
				type: 'not_available',
				label: 'Availability Conflict',
				details: statusData.availabilityMessage || 'Not available for this shift',
				severity: 'error',
			});
		}

		// Show daily overtime statuses in both day and week views — only on the causing shift
		if (statusData.isShiftCausingDailyOT) {
			const overtimeHours = statusData.todayOvertimeHours;
			employeeStatuses.push({
				type: 'in_overtime',
				label: 'In Daily Overtime',
				details: `This shift pushes employee over daily threshold with a total of ${overtimeHours.toFixed(1)} hours in overtime today`,
				severity: 'error',
			});
		} else if (
			statusData.isShiftCausingDailyApproach &&
			statusData.dailyOTWarning != null
		) {
			employeeStatuses.push({
				type: 'approaching_overtime',
				label: 'Approaching Daily Overtime',
				details: `${statusData.todayHours.toFixed(1)} hours today (warning at ${statusData.dailyOTWarning} hours)`,
				severity: 'warning',
			});
		}

		// Week view: show weekly overtime statuses
		if (calendarView === 'week' || calendarView === 'workweek') {
			if (statusData.isShiftCausingWeeklyOT) {
				const weeklyOvertimeHours = statusData.weeklyOvertimeHours;
				employeeStatuses.push({
					type: 'in_overtime',
					label: 'In Weekly Overtime',
					details: `This shift pushes employee over weekly threshold with a total of ${weeklyOvertimeHours.toFixed(1)} hours in overtime this week`,
					severity: 'error',
				});
			} else if (statusData.isShiftCausingWeeklyApproach && statusData.weeklyOTWarning) {
				// Only show on the shift that triggers the weekly overtime warning
				employeeStatuses.push({
					type: 'approaching_overtime',
					label: 'Approaching Weekly Overtime',
					details: `${statusData.totalHours.toFixed(1)} hours this week (warning at ${statusData.weeklyOTWarning} hours)`,
					severity: 'warning',
				});
			}
		}

		if (statusData.isMinor) {
			employeeStatuses.push({
				type: 'minor',
				label: 'Minor Employee',
				details: 'Employee is under the age of minor',
				severity: 'info',
			});
		}
	}

	// console.log('ScheduleEvent final employeeStatuses:', {
	// 	shiftId: original.id,
	// 	employeeName: original?.employee ? `${original.employee.firstName} ${original.employee.lastName}` : 'House Shift',
	// 	statusCount: employeeStatuses.length,
	// 	statuses: employeeStatuses,
	// });

	// Check if this is a time off event
	const isTimeOffEvent = (original as any).isTimeOff;
	const isWeekView = calendarView === 'week' || calendarView === 'workweek';
	const isDayView = calendarView === 'day';
	const timeOffEmployee =
		original?.employee || (original as any)?.timeOffRequest?.employeeRequest;
	const employeeDisplayName = timeOffEmployee
		? `${timeOffEmployee.firstName} ${timeOffEmployee.lastName}`
		: 'House Shift';
	const canDeleteThisTimeOff = Boolean(
		isTimeOffEvent &&
			onDeleteClick &&
			canDeleteCalendarTimeOff({
				event: original,
				currentUserId,
				canDeleteShifts,
				canManageAllRequests,
			})
	);
	const showDeleteButton = isTimeOffEvent
		? canDeleteThisTimeOff
		: Boolean(onDeleteClick) && canDeleteShifts;
	const showActionButtons = isTimeOffEvent
		? showDeleteButton
		: Boolean((onCopyClick && !isTimeOffEvent) || showDeleteButton);
	const shiftBackgroundColor = original?.background_color || '#446388';
	const shiftTimeLabel = formatSchedulerTimeRange(
		original.startAt || original.start,
		original.endAt || original.end
	);
	const jobCodeLabel = original?.jobCode
		? original.jobCode.jobNumber
			? `${original.jobCode.jobNumber} - ${original.jobCode.label}`
			: original.jobCode.label
		: '';
	const timeOffRangeLabel = formatTimeOffScheduleLabel(
		original.startAt || original.start,
		original.endAt || original.end
	);
	const timedTimeOff = isTimeOffEvent && isTimedTimeOff(
		original.startAt || original.start,
		original.endAt || original.end
	);

	return (
		<div
			className={getEventClassNames(calendarType, original, calendarView)}
			style={getScheduleEventStyles(original, calendarView)}
		>
			<div className="md-custom-event-wrapper">
				<div className="md-custom-event-details">
					<div className="md-custom-event-header">
						<div className="md-custom-event-title">
							{isTimeOffEvent ? (
								<div className="shift-info">
									<div className="shift-time">
										<span className="shift-time-range">
											{timedTimeOff
												? formatSchedulerTimeRange(
														original.startAt || original.start,
														original.endAt || original.end
													)
												: 'Time Off'}
										</span>
									</div>
									<div className="shift-detail-row">
										{isDayView && (
											<TruncatedTooltip
												text={employeeDisplayName}
												className="employee-name"
											/>
										)}
										<TruncatedTooltip text={timeOffRangeLabel} className="job-code" />
									</div>
								</div>
							) : isRequest ? (
								<div className="shift-info">
									<div className="shift-time">
										<span className="shift-time-range">{shiftTimeLabel}</span>
									</div>
									<div className="shift-detail-row">
										{!isWeekView && (
											<TruncatedTooltip
												text={employeeDisplayName}
												className="employee-name"
											/>
										)}
										{!isDayView && original?.jobCode && (
											<TruncatedTooltip text={jobCodeLabel} className="job-code" />
										)}
										{employeeStatuses.length > 0 && (
											<div className="shift-status-indicators">
												<EmployeeStatusIndicator
													statuses={employeeStatuses}
													employeeName={original?.employee ? employeeDisplayName : undefined}
													variant="corner"
													backgroundColor={shiftBackgroundColor}
												/>
											</div>
										)}
									</div>
								</div>
							) : (
								'Time off'
							)}
						</div>
						{showActionButtons && (
							<div className="action-buttons is-stacked">
								{!isTimeOffEvent && onCopyClick && (
									<Tooltip title="Copy" placement="left" slotProps={tooltipSlotProps}>
										<IconButton
											onMouseDown={(e) => e.stopPropagation()}
											onClick={handleCopyClick}
											size="small"
											className="action-icon-btn copy-btn"
											aria-label="Copy shift"
											sx={getShiftActionIconButtonSx(shiftBackgroundColor, 'copy')}
										>
											<ContentCopy />
										</IconButton>
									</Tooltip>
								)}
								{showDeleteButton && (
									<Tooltip title="Delete" placement="left" slotProps={tooltipSlotProps}>
										<IconButton
											onMouseDown={(e) => e.stopPropagation()}
											onClick={handleDeleteClick}
											size="small"
											className="action-icon-btn delete-btn"
											aria-label={isTimeOffEvent ? 'Delete time off' : 'Delete shift'}
											sx={getShiftActionIconButtonSx(
												isTimeOffEvent ? '#FF9800' : shiftBackgroundColor,
												'delete'
											)}
										>
											<Delete />
										</IconButton>
									</Tooltip>
								)}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

interface ScheduleResourceProps {
	resource: ScheduleResourceType;
	calendarType: string;
	showDragHandle?: boolean;
	weekStart?: string | Date | null;
}

export const ScheduleResourceComponent: React.FC<ScheduleResourceProps> = ({
	resource,
	calendarType: _calendarType,
	showDragHandle = false,
	weekStart,
}) => {
	const isDepartmentHeader = Boolean((resource as any).isDepartmentHeader);

	if (isDepartmentHeader) {
		return (
			<div className="department-header-resource">
				<span className="department-header-resource-label">{resource.name}</span>
			</div>
		);
	}

	// Use type guards to determine resource type
	const isEmployee = isEmployeeResource(resource);
	const isOpenShift = isOpenShiftResource(resource);
	const _isJobCode = isJobCodeResource(resource);
	const _isEventType = isEventTypeResource(resource);

	// Build status array for employee resources
	const employeeStatuses: EmployeeStatusInfo[] = [];
	if (isEmployee) {
		// TypeScript now knows resource is EmployeeResource
		// Check for overtime
		if (resource.hasOvertime) {
			if (resource.hasWeeklyOvertime) {
				// Always use overtimeHours which already has the correct calculation (hours - threshold)
				const overtimeHours = resource.weeklyOvertimeHours ?? 0;
				employeeStatuses.push({
					type: 'in_overtime',
					label: `In Weekly Overtime`,
					details: `Working ${overtimeHours.toFixed(1)} hours over Weekly threshold`,
					severity: 'error',
				});
			}
			else if (resource.hasDailyOvertime) {
				// Use totalDailyOvertimeHours to show total OT across all days in the week
				const overtimeHours = resource.totalDailyOvertimeHours ?? resource.overtimeHours ?? 0;
				employeeStatuses.push({
					type: 'in_overtime',
					label: `In Daily Overtime`,
					details: `Working ${overtimeHours.toFixed(1)} hours over Daily threshold`,
					severity: 'error',
				});
			}
			
		} else if (resource.isApproachingWeeklyOvertime && resource.weeklyOTWarning != null) {
			employeeStatuses.push({
				type: 'approaching_overtime',
				label: 'Approaching Weekly Overtime',
				details: `${resource.totalHours.toFixed(1)} hours this week (warning at ${resource.weeklyOTWarning} hours)`,
				severity: 'warning',
			});
		} else if (resource.isApproachingDailyOvertime && resource.dailyOTWarning != null) {
			employeeStatuses.push({
				type: 'approaching_overtime',
				label: 'Approaching Daily Overtime',
				details: `At least one day is approaching the daily overtime warning (${resource.dailyOTWarning} hours)`,
				severity: 'warning',
			});
		}

		// Check for time off
		if (resource.hasTimeOff) {
			employeeStatuses.push({
				type: 'time_off',
				label: 'Time Off Scheduled',
				details: 'Employee has approved time off during this week',
				severity: 'error',
			});
		}

		// Check for availability issues
		if (resource.hasAvailabilityIssue) {
			employeeStatuses.push({
				type: 'not_available',
				label: 'Availability Conflict',
				details: 'One or more shifts conflict with employee availability',
				severity: 'error',
			});
		}

		// Check for minor
		if (resource.isMinor) {
			employeeStatuses.push({
				type: 'minor',
				label: 'Minor Employee',
				details: 'Employee is under the age of minor',
				severity: 'info',
			});
		}
	}
	// Get display name based on resource type
	const displayName = isEmployee || isOpenShift ? (resource as any).displayName : resource.name;

	const totalHours =
		(isEmployee || isOpenShift) && 'totalHours' in resource
			? (resource as any).totalHours
			: undefined;

	const canShowDragHandle = showDragHandle && isEmployee && !isOpenShift;

	return (
		<div className={`md-work-week-cont`}>
			<div className="md-work-week-name">
				{canShowDragHandle && (
					<span
						className="schedule-resource-drag-handle"
						data-resource-id={String(resource.id)}
						title="Drag to reorder employees"
						aria-label="Drag to reorder employees"
						role="button"
						tabIndex={0}
						onMouseDown={(event) => event.stopPropagation()}
					>
						<DragIndicator fontSize="inherit" />
					</span>
				)}
				{isEmployee || isOpenShift ? (
					<div className="employee-resource">
						<div className="employee-name-row">
							<TruncatedTooltip text={displayName} className="employee-name" />
							{isEmployee && (
								<EmployeeAvailabilityIndicator
									employeeName={displayName}
									userAvailability={
										(resource as any).userAvailability ||
										(resource as any).employee?.userAvailability
									}
									weekStart={weekStart}
								/>
							)}
						</div>
						{totalHours !== undefined && (
							<div className="employee-hours">{formatSchedulerHours(totalHours)}</div>
						)}
						{employeeStatuses.length > 0 && (
							<div className="employee-status-row">
								<EmployeeStatusIndicator statuses={employeeStatuses} employeeName={displayName} />
							</div>
						)}
					</div>
				) : (
					<TruncatedTooltip text={resource.name} className="jobcode-resource" />
				)}
			</div>
		</div>
	);
};

// Export with backward compatible name
export const ScheduleResource = ScheduleResourceComponent;

export default ScheduleEvent;
